import { useCallback, useState } from "react";

import { useFormContext } from "react-hook-form";

import {
  Box,
  Button,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { Modal } from "@covertree/ui";

import {
  selectBasicQuestions,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { FormRadioGroup } from "@covertree/ui";

import {
  fixturesAndFinishesTypes,
  HomeDetailsForm,
  houseDetailsDict,
} from "../validatorsAndTypes/homeDetailsAndConstructionForm";

interface FixturesAndFinishesRadioGroupProps {
  showError?: boolean;
  isD2C?: boolean;
  onOptionSelect?: (option: unknown) => void;
}

export function FixturesAndFinishesRadioGroup({
  showError,
  onOptionSelect,
  isD2C,
}: FixturesAndFinishesRadioGroupProps) {
  const { homeFixtures } = useNewBusinessSelector(selectBasicQuestions);

  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<HomeDetailsForm>();

  const selected = watch("homeFixtures");

  const [isModalOpened, setIsModalOpen] = useState(false);

  const changeModalOpenStatus = useCallback(
    (newStatus: boolean) => () => setIsModalOpen(newStatus),
    []
  );

  return (
    <>
      <FormRadioGroup
        name="homeFixtures"
        control={control}
        selected={selected}
        label={
          homeFixtures?.label ??
          "How would you describe your home's fixtures & finishes?"
        }
        items={fixturesAndFinishesTypes}
        direction="column"
        onOptionSelect={onOptionSelect}
      />
      {showError ? (
        <Box>
          <FormHelperText error>
            {errors["homeFixtures"]?.message}
          </FormHelperText>
        </Box>
      ) : null}
      <Box mt={3}>
        <Button
          size="small"
          variant="outlined"
          onClick={changeModalOpenStatus(true)}
          data-testid="how-are-these-different-link">
          How are these different?
        </Button>
      </Box>
      <Modal
        title="How are these packages different?"
        open={isModalOpened}
        onClose={changeModalOpenStatus(false)}
        sx={{
          "& .MuiPaper-root > .MuiBox-root:nth-of-type(2)": {
            padding: (theme) => theme.spacing(3, 3, 0),
          },
        }}>
        {isDesktop ? (
          <Table
            sx={(theme) => ({
              marginLeft: theme.spacing(-1.5),
              marginRight: theme.spacing(-1.5),
            })}>
            <TableHead>
              <TableRow data-testid="table-head-row">
                <TableCell
                  sx={{
                    width: "22%",
                    verticalAlign: "bottom",
                  }}>
                  <Typography
                    sx={(theme) => ({
                      color: theme.palette.info.main,
                      fontSize: "1rem",
                    })}>
                    Building types
                  </Typography>
                </TableCell>
                {fixturesAndFinishesTypes.map((type) => (
                  <TableCell key={type.title}>
                    <Box mb={2}>
                      <img src={type.image} alt={type.imageAlt} />
                    </Box>
                    <Typography>{type.title}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {houseDetailsDict.map((prop) => {
                const rowData = fixturesAndFinishesTypes.map(
                  (entry) => entry[prop]
                );

                return (
                  <TableRow
                    key={prop}
                    sx={{
                      verticalAlign: "top",
                    }}
                    data-testid="table-body-row">
                    <TableCell>{prop}</TableCell>
                    {rowData.map((data) => (
                      <TableCell key={data.name}>
                        <Typography>{data.name}</Typography>
                        {data.description && (
                          <Typography
                            sx={(theme) => ({
                              color: theme.palette.info.main,
                              fontSize: "1rem",
                              marginTop: theme.spacing(1),
                            })}>
                            {data.description}
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}

              {isD2C && (
                <TableRow>
                  <TableCell />
                  {fixturesAndFinishesTypes.map((type) => (
                    <TableCell key={`select-${type.title}`}>
                      <Button
                        size="small"
                        variant={
                          type.value === selected ? "contained" : "outlined"
                        }
                        fullWidth
                        onClick={() => {
                          onOptionSelect?.(type.value);
                          changeModalOpenStatus(false);
                        }}
                        sx={{ fontSize: "1rem" }}>
                        Select
                      </Button>
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableBody>
          </Table>
        ) : (
          <Box
            display="flex"
            sx={{
              flexDirection: "column",
              height: "100%",
              maxHeight: "80vh",
              position: "relative",
            }}>
            <Box
              sx={{
                overflowY: "auto",
                flexGrow: 1,
                marginBottom: isD2C ? 9 : 0,
              }}>
              <Table>
                <TableBody>
                  <TableRow>
                    {fixturesAndFinishesTypes.map((type) => (
                      <TableCell
                        key={type.title}
                        align="center"
                        sx={{ width: "33.33%" }}>
                        <Box mb={2} sx={{ height: { xs: 80, md: "auto" } }}>
                          <img
                            src={type.image}
                            alt={type.imageAlt}
                            style={{ maxWidth: "100%", height: "auto" }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: {
                              xs: 16,
                              md: "1rem",
                            },
                            minHeight: { xs: 48, md: "auto" },
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}>
                          {type.title}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>

              {houseDetailsDict.map((prop) => (
                <Box key={prop}>
                  <Typography
                    sx={(theme) => ({
                      borderTop: `1px solid ${theme.palette.grey[100]}`,
                      borderBottom: `1px solid ${theme.palette.grey[100]}`,
                      padding: 2,
                      fontSize: {
                        xs: 16,
                        md: "1rem",
                      },
                      textAlign: "center",
                    })}>
                    {prop}
                  </Typography>
                  <Table>
                    <TableBody>
                      <TableRow>
                        {fixturesAndFinishesTypes.map((entry) => (
                          <TableCell
                            key={`${prop}-${entry.title}`}
                            sx={{
                              width: "33.33%",
                              paddingX: 0.5,
                              verticalAlign: "top",
                            }}>
                            <Typography
                              sx={{
                                wordBreak: "break-word",
                                fontSize: {
                                  xs: 11,
                                  md: "1rem",
                                },
                              }}>
                              {entry[prop].name}
                            </Typography>
                            {entry[prop].description && (
                              <Typography
                                sx={(theme) => ({
                                  color: theme.palette.info.main,
                                  fontSize: {
                                    xs: 12,
                                    md: "1rem",
                                  },
                                  marginTop: theme.spacing(1),
                                })}>
                                {entry[prop].description}
                              </Typography>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              ))}
            </Box>
            {isD2C && (
              <Box
                sx={(theme) => ({
                  display: "flex",
                  justifyContent: "space-evenly",
                  position: "absolute",
                  width: "100%",
                  overflow: "hidden",
                  bottom: 0,
                  left: 0,
                  mt: 2,
                  background: theme.palette.background.paper,
                })}>
                {fixturesAndFinishesTypes.map((type) => (
                  <Box
                    key={`select-mobile-${type.title}`}
                    sx={(theme) => ({
                      flex: 1,
                      maxWidth: "33%",
                      padding: theme.spacing(1),
                      textAlign: "center",
                    })}>
                    <Button
                      sx={{ maxWidth: "160px" }}
                      fullWidth
                      size="small"
                      color="primary"
                      variant={
                        type.value === selected ? "contained" : "outlined"
                      }
                      onClick={() => {
                        onOptionSelect?.(type.value);
                        changeModalOpenStatus(false);
                      }}>
                      Select
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Modal>
    </>
  );
}
